import { useEffect, useState } from "react";

const toArabicDigits = (n) => String(n).replace(/[0-9]/g, (d) => "٠١٢٣٤٥٦٧٨٩"[Number(d)]);
const pad2 = (n) => (n < 10 ? "0" + n : String(n));

const isoToKsaDeadline = (iso) => {
  // Treat iso as a KSA date (end of day). 23:59:59 KSA = 20:59:59 UTC
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  return new Date(`${iso}T20:59:59Z`).getTime();
};

const useCountdown = (deadline) => {
  const compute = () => {
    if (!deadline) return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    const diff = deadline - Date.now();
    if (diff <= 0) return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);
    return { expired: false, days, hours, minutes, seconds };
  };

  const [state, setState] = useState(compute);
  useEffect(() => {
    const id = setInterval(() => setState(compute()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);
  return state;
};

const Cell = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div
      className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center font-bold text-xl sm:text-2xl"
      style={{
        background: "rgba(212, 175, 55, 0.12)",
        border: "1px solid rgba(212, 175, 55, 0.45)",
        color: "#D4AF37",
        fontFamily: "Cairo",
        textShadow: "0 1px 10px rgba(212,175,55,0.35)",
      }}
    >
      {toArabicDigits(pad2(value))}
    </div>
    <span className="text-[10px] sm:text-[11px] mt-1.5 tracking-wider" style={{ color: "#CBD5E1", fontFamily: "Cairo" }}>
      {label}
    </span>
  </div>
);

const Separator = () => (
  <span className="font-bold text-xl sm:text-2xl pb-5 opacity-60" style={{ color: "#D4AF37" }}>
    :
  </span>
);

/**
 * Gold countdown — counts down to the provided ISO date (treated as end-of-day in KSA).
 * Defaults to Nov 8, 2026 (registration close) when no prop is given.
 */
const KhutaCountdown = ({ targetIso, expiredMessage = "انتهت الفترة." }) => {
  const deadline = isoToKsaDeadline(targetIso || "2026-11-08");
  const { expired, days, hours, minutes, seconds } = useCountdown(deadline);

  if (expired) {
    return (
      <div
        className="rounded-xl px-4 py-3 text-center text-sm"
        style={{
          background: "rgba(212, 175, 55, 0.08)",
          border: "1px dashed rgba(212, 175, 55, 0.4)",
          color: "#D4AF37",
          fontFamily: "Cairo",
        }}
        data-testid="khuta-countdown-expired"
      >
        {expiredMessage}
      </div>
    );
  }

  return (
    <div dir="rtl" data-testid="khuta-countdown">
      <div className="flex items-end gap-2 sm:gap-3 justify-center" style={{ direction: "ltr" }}>
        <Cell value={days} label="يوم" />
        <Separator />
        <Cell value={hours} label="ساعة" />
        <Separator />
        <Cell value={minutes} label="دقيقة" />
        <Separator />
        <Cell value={seconds} label="ثانية" />
      </div>
    </div>
  );
};

export default KhutaCountdown;
